import React from 'react';
import { NswTerminationReason } from '@app/config';
import Card, { CardShadow, CardStyles } from '@app/common/components/cards/Card';
import styles from './SupportingDocumentFullCriteria.module.scss';

interface SupportingDocumentFullCriteriaProps {
    reasonForTermination?: NswTerminationReason;
}

const SupportingDocumentFullCriteria: React.FC<SupportingDocumentFullCriteriaProps> = ({ reasonForTermination }) => {
    switch (reasonForTermination) {
        case NswTerminationReason.HasSold:
            return (
                <Card
                    className={styles.requirements}
                    style={CardStyles.SQUARE}
                    shadow={CardShadow.SMALL}
                    animate={false}
                >
                    <h2 className={styles.requirementTitle}>
                        The Landlord must provide the tenant with one of the following when giving this termination
                        notice:
                    </h2>
                    <div className={styles.requirementListContainer}>
                        <div className={styles.requirement}>
                            Copy of the contract (or part) for the sale of the premises showing:
                        </div>
                        <ul className={styles.requirementList}>
                            <li>Vendor's name</li>
                            <li>Solicitor or conveyancer's name of the vendor (if applicable).</li>
                            <li>Agent's name (if applicable).</li>
                            <li>Purchaser's name.</li>
                            <li>Solicitor or conveyancer's name for the purchaser (if applicable).</li>
                            <li>Address of the premises, including lot and plan numbers.</li>
                            <li>Date the contract was signed.</li>
                            <li>Proposed completion date.</li>
                            <li>Requirement for vacant possession.</li>
                        </ul>
                    </div>
                    <div className={styles.operator}>OR</div>
                    <div className={styles.requirementListContainer}>
                        <div className={styles.requirement}>
                            Written statement from the vendor's solicitor or conveyancer stating:
                        </div>
                        <ul className={styles.requirementList}>
                            <li>Contract for sale has been entered into.</li>
                            <li>Vendor's name.</li>
                            <li>Address of the premises, including lot and plan numbers.</li>
                            <li>Date the contract was signed.</li>
                            <li>Proposed completion date.</li>
                            <li>Requirement for vacant possession.</li>
                        </ul>
                    </div>
                </Card>
            );
        case NswTerminationReason.BeingSold:
            return (
                <Card
                    className={styles.requirements}
                    style={CardStyles.SQUARE}
                    shadow={CardShadow.SMALL}
                    animate={false}
                >
                    <h2 className={styles.requirementTitle}>
                        The Landlord must provide the tenant with one of the following when giving this termination
                        notice:
                    </h2>
                    <div className={styles.requirementListContainer}>
                        <div className={styles.requirement}>Copy of a proposed contract for sale showing:</div>
                        <ul className={styles.requirementList}>
                            <li>Vendor's name</li>
                            <li>Solicitor or conveyancer's name of the vendor (if applicable).</li>
                            <li>Agent's name (if applicable).</li>
                            <li>Address of the premises, including lot and plan numbers.</li>
                            <li>Requirement for vacant possession.</li>
                            <li>
                                Attachment of prescribed documents in a contract for sale pursuant to the Conveyancing
                                Act.
                            </li>
                        </ul>
                    </div>
                    <div className={styles.operator}>OR</div>
                    <div className={styles.requirementListContainer}>
                        <div className={styles.requirement}>
                            Copy of a sales agency agreement (or part) between the landlord and the sales agent showing:
                        </div>
                        <ul className={styles.requirementList}>
                            <li>The names of the parties to the agency agreement</li>
                            <li>Address of the premises</li>
                            <li>Real estate agents licence number</li>
                            <li>The duration of the agency agreement</li>
                        </ul>
                    </div>
                </Card>
            );
        case NswTerminationReason.Renovations:
            return (
                <Card
                    className={styles.requirements}
                    style={CardStyles.SQUARE}
                    shadow={CardShadow.SMALL}
                    animate={false}
                >
                    <h2 className={styles.requirementTitle}>
                        The Landlord must provide the tenant with the following when giving this termination
                        notice:
                    </h2>
                    <div className={styles.requirementListContainer}>
                        <div className={styles.requirement}>Written statement by the landlord:</div>
                        <ul className={styles.requirementList}>
                            <li>Signed and dated by the landlord.</li>
                            <li>Explanation of why the renovations or repairs are significant.</li>
                            <li>Explanation of why the property must be vacant for the works.</li>
                            <li>Proposed commencement date of the works.</li>
                        </ul>
                    </div>
                </Card>
            );
        case NswTerminationReason.Demolition:
            return (
                <Card
                    className={styles.requirements}
                    style={CardStyles.SQUARE}
                    shadow={CardShadow.SMALL}
                    animate={false}
                >
                    <h2 className={styles.requirementTitle}>
                        The Landlord must provide the tenant with the following when giving this termination
                        notice:
                    </h2>
                    <div className={styles.requirementListContainer}>
                        <div className={styles.requirement}>A written statement from the landlord that:</div>
                        <ul className={styles.requirementList}>
                            <li>Signed and dated by the landlord</li>
                            <li>States the proposed commencement date of the demolition</li>
                        </ul>
                    </div>

                    <div className={styles.operator}>AND</div>

                    <div className={styles.requirementListContainer}>
                        <div className={styles.requirement}>
                            A copy of the contract (or part) for the demolition that shows:
                        </div>
                        <ul className={styles.requirementList}>
                            <li>Names of each of the parties to the contract</li>
                            <li>Description of the demolition and address of the premises</li>
                            <li>Commencement date of the demolition</li>
                            <li>
                                Demolition licence number of the person carrying out the licensed demolition work (if
                                applicable)
                            </li>
                        </ul>
                    </div>

                    <div className={styles.operator}>AND</div>

                    <div className={styles.requirement}>If applicable, a copy of the development consent</div>

                    <div className={styles.operator}>AND</div>

                    <div className={styles.requirement}>If applicable, a copy of the development control order</div>
                </Card>
            );
        case NswTerminationReason.ChangeOfUse:
            return (
                <Card
                    className={styles.requirements}
                    style={CardStyles.SQUARE}
                    shadow={CardShadow.SMALL}
                    animate={false}
                >
                    <h2 className={styles.requirementTitle}>
                        The Landlord must provide the tenant with the following when giving this termination
                        notice:
                    </h2>
                    <div className={styles.requirementListContainer}>
                        <div className={styles.requirement}>Written statement from the landlord that is:</div>
                        <ul className={styles.requirementList}>
                            <li>Signed and dated by the landlord</li>
                            <li>
                                States the purpose for which the premises will be used for after the termination date
                            </li>
                            <li>
                                States that the premises will not be used as rented residential premises for at least 12
                                months after the termination date
                            </li>
                        </ul>
                    </div>

                    <div className={styles.operator}>AND</div>

                    <div className={styles.requirement}>If the premises is to be used to carry on a business:</div>
                    <ul className={styles.requirementList}>
                        <li>The ABN on the business</li>
                        <li>
                            Copy of the development consent that relates to the use of the premises for carrying on the
                            business
                        </li>
                        <li>The licence number of a licence that will be used in carrying on the business</li>
                    </ul>

                    <div className={styles.requirement}>
                        If the premises is being used as a short term rental accommodation, proof of the premises being
                        included in the STRA register.
                    </div>
                </Card>
            );
        case NswTerminationReason.LandlordResidence:
            return (
                <Card
                    className={styles.requirements}
                    style={CardStyles.SQUARE}
                    shadow={CardShadow.SMALL}
                    animate={false}
                >
                    <h2 className={styles.requirementTitle}>
                        The Landlord must provide the tenant with one of the following when giving this termination
                        notice:
                    </h2>
                    <div className={styles.requirementListContainer}>
                        <div className={styles.requirement}>A written statement from the landlord:</div>
                        <ul className={styles.requirementList}>
                            <li>Signed and dated by the landlord</li>
                            <li>
                                State that the notice is given on the ground that the landlord will reside at the
                                premises for at least 6 months OR state that the relevant person (who is not the
                                landlord) will reside at the premises for at least 6 months
                            </li>
                            <li>State the relationship between the relevant person and the landlord</li>
                        </ul>
                    </div>
                    <div className={styles.operator}>OR</div>
                    <div className={styles.requirementListContainer}>
                        <div className={styles.requirement}>
                            A written statement from the relevant person to reside at the premises:
                        </div>
                        <ul className={styles.requirementList}>
                            <li>Signed and dated by the relevant person</li>
                            <li>
                                State that the relevant person (who is not the landlord) will reside at the premises for
                                at least 6 months
                            </li>
                            <li>State the relationship between the relevant person and the landlord</li>
                        </ul>
                    </div>
                </Card>
            );
        default:
            return null;
    }
};

export default SupportingDocumentFullCriteria;
